import { useState } from 'react';
import { Bell, BellRing, Monitor, Send, Smartphone, Tablet } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchInput } from '@/components/ui/search-input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { usePushEvents, usePushStats, usePushSubscriptions, useSendTestPush, useUsersWithPush } from '@/hooks/usePushAdmin';

type StatusFilter = 'todos' | 'PENDING' | 'DELIVERED' | 'PARTIAL' | 'FAILED';
type TabView = 'historico' | 'dispositivos' | 'enviar';

const DEFAULT_PAGE_SIZE = 20;

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  DELIVERED: 'Entregue',
  PARTIAL: 'Parcial',
  FAILED: 'Falha',
};

function statusBadgeVariant(status: string): 'default' | 'outline' | 'secondary' | 'destructive' {
  switch (status) {
    case 'DELIVERED': return 'default';
    case 'PARTIAL': return 'secondary';
    case 'FAILED': return 'destructive';
    default: return 'outline';
  }
}

function getDeviceInfo(userAgent: string | null): { icon: typeof Monitor; label: string } {
  if (!userAgent) return { icon: Monitor, label: 'Desconhecido' };
  const ua = userAgent.toLowerCase();
  if (/iphone|android.*mobile/.test(ua)) return { icon: Smartphone, label: 'Celular' };
  if (/ipad|android(?!.*mobile)|tablet/.test(ua)) return { icon: Tablet, label: 'Tablet' };
  if (/edg/.test(ua)) return { icon: Monitor, label: 'Edge' };
  if (/chrome/.test(ua)) return { icon: Monitor, label: 'Chrome' };
  if (/firefox/.test(ua)) return { icon: Monitor, label: 'Firefox' };
  if (/safari/.test(ua)) return { icon: Monitor, label: 'Safari' };
  return { icon: Monitor, label: 'Desktop' };
}

export default function PushNotificationsPage() {
  const { showSuccess, showError } = useNotification();
  const [tab, setTab] = useState<TabView>('historico');

  // --- Histórico state ---
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // --- Dispositivos state ---
  const [devSearch, setDevSearch] = useState('');
  const [devPage, setDevPage] = useState(1);
  const [devPageSize, setDevPageSize] = useState(DEFAULT_PAGE_SIZE);

  // --- Enviar state ---
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<{ id: string; nome: string; email: string; devices: number } | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');

  // --- Queries ---
  const { data: stats } = usePushStats();
  const statusParam = statusFilter === 'todos' ? undefined : statusFilter;
  const { data: eventsData, isLoading, error } = usePushEvents({
    status: statusParam,
    search: search || undefined,
    page,
    page_size: pageSize,
  });
  const { data: subsData, isLoading: isLoadingSubs, error: subsError } = usePushSubscriptions({
    search: devSearch || undefined,
    page: devPage,
    page_size: devPageSize,
  });
  const { data: users = [], isLoading: isLoadingUsers } = useUsersWithPush(userSearch);
  const sendMutation = useSendTestPush();

  const items = eventsData?.items ?? [];
  const total = eventsData?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const subItems = subsData?.items ?? [];
  const subTotal = subsData?.total ?? 0;
  const subTotalPages = Math.ceil(subTotal / devPageSize);

  const totalEvents = (stats?.pending ?? 0) + (stats?.delivered ?? 0) + (stats?.partial ?? 0) + (stats?.failed ?? 0);

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as StatusFilter);
    setPage(1);
  };

  const handleSend = () => {
    if (!selectedUser || !title.trim() || !body.trim()) return;

    sendMutation.mutate(
      { user_id: selectedUser.id, title: title.trim(), body: body.trim(), url: url.trim() || undefined },
      {
        onSuccess: (res) => {
          showSuccess(`Notificação enviada para ${selectedUser.nome} (${res.devices_count} dispositivo(s))`);
          setTitle('');
          setBody('');
          setUrl('');
          setSelectedUser(null);
          setUserSearch('');
          setTab('historico');
        },
        onError: (err) => notifyMutationError(err, showError),
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notificações Push"
        subtitle="Monitore notificações enviadas e gerencie dispositivos."
        icon={<BellRing />}
      />

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabView)}>
        <TabsList>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
          <TabsTrigger value="dispositivos">
            <Smartphone className="mr-1 h-3.5 w-3.5" />
            Dispositivos ({stats?.active_subscriptions ?? 0})
          </TabsTrigger>
          <TabsTrigger value="enviar">
            <Send className="mr-1 h-3.5 w-3.5" />
            Enviar teste
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* === Aba Histórico === */}
      {tab === 'historico' && (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <SearchInput
              value={search}
              onChange={(val) => { setSearch(val); setPage(1); }}
              placeholder="Buscar por título ou conteúdo..."
              className="w-full sm:max-w-sm"
            />
            <Tabs value={statusFilter} onValueChange={handleStatusChange}>
              <TabsList>
                <TabsTrigger value="todos">Todos ({totalEvents})</TabsTrigger>
                <TabsTrigger value="DELIVERED">Entregues ({stats?.delivered ?? 0})</TabsTrigger>
                <TabsTrigger value="PARTIAL">Parciais ({stats?.partial ?? 0})</TabsTrigger>
                <TabsTrigger value="FAILED">Falhas ({stats?.failed ?? 0})</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {isLoading && <Spinner size={32} label="Carregando notificações" />}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{classifyError(error).message}</AlertDescription>
            </Alert>
          )}
          {!isLoading && !error && items.length === 0 && (
            <EmptyState
              icon={<Bell />}
              title="Nenhuma notificação encontrada"
              description="Nenhuma notificação push foi enviada ainda. Use a aba 'Enviar teste' para disparar a primeira."
            />
          )}
          {items.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Destinatário</TableHead>
                      <TableHead className="hidden sm:table-cell">Título</TableHead>
                      <TableHead className="hidden lg:table-cell">Conteúdo</TableHead>
                      <TableHead className="hidden sm:table-cell">Status</TableHead>
                      <TableHead className="hidden md:table-cell text-center">Dispositivos</TableHead>
                      <TableHead className="hidden md:table-cell">Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((e) => {
                      const dateValue = e.sent_at ?? e.created_at;
                      return (
                      <TableRow key={e.id}>
                        <TableCell className="text-sm">
                          <div className="flex flex-col gap-0.5">
                            <span>{e.user_name || '—'}</span>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                              <Badge variant={statusBadgeVariant(e.status)} className="text-[11px] px-1.5 py-0">
                                {STATUS_LABELS[e.status] || e.status}
                              </Badge>
                              {dateValue && <span>{new Date(dateValue).toLocaleDateString('pt-BR')}</span>}
                              {e.devices_total > 0 && <span>{e.devices_success}/{e.devices_total} disp.</span>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden break-words text-sm sm:table-cell">{e.title}</TableCell>
                        <TableCell className="hidden break-words text-sm lg:table-cell">
                          {e.body}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge variant={statusBadgeVariant(e.status)}>
                            {STATUS_LABELS[e.status] || e.status}
                          </Badge>
                          {e.error && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="ml-1 cursor-help text-xs text-destructive">(?)</span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm font-mono text-xs">{e.error}</TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-center text-sm">
                          {e.devices_total > 0 ? (
                            <span>{e.devices_success}/{e.devices_total}</span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground whitespace-nowrap">
                          {e.sent_at
                            ? new Date(e.sent_at).toLocaleString('pt-BR')
                            : e.created_at
                              ? new Date(e.created_at).toLocaleString('pt-BR')
                              : '—'}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>

              {totalPages > 1 && (
                <div className="border-t p-2">
                  <DataTablePagination
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    pages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                  />
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* === Aba Dispositivos === */}
      {tab === 'dispositivos' && (
        <>
          <SearchInput
            value={devSearch}
            onChange={(val) => { setDevSearch(val); setDevPage(1); }}
            placeholder="Buscar por nome ou email..."
            className="w-full sm:max-w-sm"
          />

          {isLoadingSubs && <Spinner size={32} label="Carregando dispositivos" />}
          {subsError && (
            <Alert variant="destructive">
              <AlertDescription>{classifyError(subsError).message}</AlertDescription>
            </Alert>
          )}
          {!isLoadingSubs && !subsError && subItems.length === 0 && (
            <EmptyState
              icon={<Smartphone />}
              title="Nenhum dispositivo registrado"
              description="Nenhum usuário ativou notificações push ainda."
            />
          )}
          {subItems.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuário</TableHead>
                      <TableHead className="hidden sm:table-cell">Dispositivo</TableHead>
                      <TableHead className="hidden lg:table-cell">Registrado em</TableHead>
                      <TableHead className="hidden md:table-cell">Último envio</TableHead>
                      <TableHead className="hidden sm:table-cell text-center">Falhas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subItems.map((s) => {
                      const { icon: DeviceIcon, label: deviceLabel } = getDeviceInfo(s.user_agent);
                      return (
                        <TableRow key={s.id}>
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <p className="text-sm font-medium">{s.user_name}</p>
                              <p className="text-xs text-muted-foreground">{s.user_email}</p>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                                <span className="inline-flex items-center gap-1">
                                  <DeviceIcon className="h-3 w-3" />
                                  {deviceLabel}
                                </span>
                                <span>
                                  {s.last_success_at
                                    ? `Últ. ${new Date(s.last_success_at).toLocaleDateString('pt-BR')}`
                                    : 'Nunca enviado'}
                                </span>
                                {s.failure_count > 0 && (
                                  <Badge variant="destructive" className="text-[11px] px-1.5 py-0">
                                    {s.failure_count} falha{s.failure_count > 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <div className="flex items-center gap-2">
                              <DeviceIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{deviceLabel}</span>
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-muted-foreground whitespace-nowrap">
                            {s.created_at ? new Date(s.created_at).toLocaleString('pt-BR') : '—'}
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-sm text-muted-foreground whitespace-nowrap">
                            {s.last_success_at ? new Date(s.last_success_at).toLocaleString('pt-BR') : 'Nunca'}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell text-center text-sm">
                            {s.failure_count > 0 ? (
                              <Badge variant="destructive">{s.failure_count}</Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>

              {subTotalPages > 1 && (
                <div className="border-t p-2">
                  <DataTablePagination
                    page={devPage}
                    pageSize={devPageSize}
                    total={subTotal}
                    pages={subTotalPages}
                    onPageChange={setDevPage}
                    onPageSizeChange={(size) => { setDevPageSize(size); setDevPage(1); }}
                  />
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* === Aba Enviar Teste === */}
      {tab === 'enviar' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Enviar notificação de teste</CardTitle>
            <CardDescription>
              A notificação será enviada via Web Push para todos os dispositivos registrados do usuário.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-w-lg">
              {/* Seleção de usuário */}
              <div className="space-y-2">
                <Label>Usuário</Label>
                {selectedUser ? (
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <p className="text-sm font-medium">{selectedUser.nome}</p>
                      <p className="text-xs text-muted-foreground">{selectedUser.email} · {selectedUser.devices} dispositivo(s)</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setUserSearch(''); }}>
                      Trocar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <SearchInput
                      value={userSearch}
                      onChange={setUserSearch}
                      placeholder="Buscar por nome ou email..."
                      className="w-full"
                    />
                    {isLoadingUsers && userSearch.length >= 2 && (
                      <Spinner size={16} label="Buscando usuários" />
                    )}
                    {users.length > 0 && (
                      <div className="rounded-md border divide-y max-h-48 overflow-y-auto">
                        {users.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            className="flex w-full items-center justify-between p-2.5 text-left hover:bg-muted/50 transition-colors"
                            onClick={() => { setSelectedUser(u); setUserSearch(''); }}
                          >
                            <div>
                              <p className="text-sm font-medium">{u.nome}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              <Smartphone className="mr-1 h-3 w-3" />
                              {u.devices}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}
                    {userSearch.length >= 2 && !isLoadingUsers && users.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        Nenhum usuário com push ativo encontrado.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Título */}
              <div className="space-y-2">
                <Label htmlFor="push-title">Título</Label>
                <Input
                  id="push-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Notificação de teste"
                  maxLength={100}
                />
              </div>

              {/* Body */}
              <div className="space-y-2">
                <Label htmlFor="push-body">Conteúdo</Label>
                <Textarea
                  id="push-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Ex: Esta é uma notificação de teste do JurisApp"
                  maxLength={500}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground text-right">{body.length}/500</p>
              </div>

              {/* URL */}
              <div className="space-y-2">
                <Label htmlFor="push-url">URL (opcional)</Label>
                <Input
                  id="push-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Ex: /app/processos/123"
                />
                <p className="text-xs text-muted-foreground">Página que abre ao clicar na notificação</p>
              </div>

              {/* Botão enviar */}
              <Button
                onClick={handleSend}
                disabled={!selectedUser || !title.trim() || !body.trim() || sendMutation.isPending}
                className="w-full sm:w-auto"
              >
                {sendMutation.isPending ? (
                  <Spinner size={16} className="mr-2 text-inherit" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Enviar notificação
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
